/**
 * IANA-timezone-aware date math for the digest module.
 *
 * Everything in `calendar/dates.ts` deliberately uses the *runtime's* local time (the
 * viewer's own clock) because every calendar view renders in the viewer's timezone. The
 * digest scheduler is different: it runs once, server-side, and has to know what "today"
 * and "7:00" mean *in the household's timezone* regardless of what timezone the VPS
 * process happens to be running in (ROADMAP §1.5, §2 "household timezone America/Denver
 * default"). So this file exists solely to answer "what UTC instant is local midnight (or
 * 7am) in `tz`", independent of `Intl`'s implicit-local-zone shortcuts.
 */
/** Household default (ROADMAP §2 pulled-forward digest paragraph). */
export const DEFAULT_HOUSEHOLD_TZ = 'America/Denver';
const partsFormatterCache = new Map();
function partsFormatter(tz) {
    let formatter = partsFormatterCache.get(tz);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        partsFormatterCache.set(tz, formatter);
    }
    return formatter;
}
/** The wall-clock date/time `date` reads as inside `tz`. */
export function getZonedDateParts(date, tz) {
    const parts = partsFormatter(tz).formatToParts(date);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? '0');
    return {
        year: get('year'),
        month: get('month'),
        day: get('day'),
        hour: get('hour'),
        minute: get('minute'),
        second: get('second'),
    };
}
/**
 * The UTC instant at which `tz`'s wall clock reads `year-month-day hour:minute:second`.
 * Standard two-pass convergence: guess the instant by treating the wall time as if it were
 * UTC, see how far that guess's *own* zoned reading drifts from the target, and correct —
 * which self-corrects across DST transitions without a timezone database of our own.
 */
export function zonedTimeToUtc(parts, tz) {
    const hour = parts.hour ?? 0;
    const minute = parts.minute ?? 0;
    const second = parts.second ?? 0;
    // The wall-clock numbers, read as if they were already a UTC instant — both the fixed
    // target we're converging on, and (for iteration 0) the first guess at the real instant.
    const targetAsUtcMs = Date.UTC(parts.year, parts.month - 1, parts.day, hour, minute, second);
    let guessMs = targetAsUtcMs;
    for (let i = 0; i < 2; i += 1) {
        const seen = getZonedDateParts(new Date(guessMs), tz);
        const seenAsUtcMs = Date.UTC(seen.year, seen.month - 1, seen.day, seen.hour, seen.minute, seen.second);
        // `offset` is "what tz's wall clock currently reads, minus the real instant" — i.e. the
        // zone's UTC offset in ms at `guessMs`. Re-deriving it from `targetAsUtcMs` (not the
        // previous guess) each pass is what makes this converge instead of oscillate.
        const offsetMs = seenAsUtcMs - guessMs;
        const nextGuessMs = targetAsUtcMs - offsetMs;
        if (nextGuessMs === guessMs)
            break;
        guessMs = nextGuessMs;
    }
    return new Date(guessMs);
}
/** `YYYY-MM-DD` as read on `tz`'s wall clock — the digest's notion of "today". */
export function dateKeyInTz(date, tz) {
    const parts = getZonedDateParts(date, tz);
    return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
}
/** The UTC instant of local midnight, on the local calendar day `date` falls on in `tz`. */
export function startOfDayInTz(date, tz) {
    const parts = getZonedDateParts(date, tz);
    return zonedTimeToUtc({ year: parts.year, month: parts.month, day: parts.day }, tz);
}
/** Local midnight `offsetDays` away from the day `date` falls on in `tz` (may be negative). */
export function shiftedStartOfDayInTz(date, offsetDays, tz) {
    const parts = getZonedDateParts(date, tz);
    // `Date.UTC`/`zonedTimeToUtc` both normalise an out-of-range day (e.g. day 0 or day 32),
    // so "day + offset" needs no manual month/year rollover here.
    return zonedTimeToUtc({ year: parts.year, month: parts.month, day: parts.day + offsetDays }, tz);
}
/** The UTC instant `hh:mm` falls on, on the local calendar day `date` falls on in `tz`. */
export function timeOnDateInTz(date, hhmm, tz) {
    const [hourStr, minuteStr] = hhmm.split(':');
    const hour = Number(hourStr ?? 0);
    const minute = Number(minuteStr ?? 0);
    const parts = getZonedDateParts(date, tz);
    return zonedTimeToUtc({ year: parts.year, month: parts.month, day: parts.day, hour, minute }, tz);
}
//# sourceMappingURL=tz.js.map